const MAX_CENTS = 2147483647;

export function parseCashCents(value) {
    if (typeof value !== 'string' && !Number.isSafeInteger(value)) return null;
    const input = String(value).trim();
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(input)) return null;
    const [euros, decimals = ''] = input.replace(',', '.').split('.');
    const digits =
        `${euros}${decimals.padEnd(2, '0')}`.replace(/^0+/, '') || '0';
    const limit = String(MAX_CENTS);
    if (
        digits.length > limit.length ||
        (digits.length === limit.length && digits > limit)
    )
        return null;
    return Number(digits);
}

export function cashTenderSufficient(received, dueCents) {
    const cents = parseCashCents(received);
    return (
        Number.isInteger(dueCents) &&
        dueCents > 0 &&
        cents !== null &&
        cents >= dueCents
    );
}

export function splitCashSufficient(rows) {
    return (
        Array.isArray(rows) &&
        rows.every(
            (row) =>
                row.method !== 'cash' ||
                cashTenderSufficient(
                    row.cash_received,
                    parseCashCents(row.amount),
                ),
        )
    );
}

export function cashTenderSummary(rows) {
    const cash = Array.isArray(rows)
        ? rows.filter((row) => row?.method === 'cash')
        : [];
    let applied = 0;
    let received = 0;
    let change = 0;
    let missing = 0;
    let invalid = false;
    for (const row of cash) {
        const amount = parseCashCents(row.amount);
        const tender = parseCashCents(row.cash_received);
        invalid ||= amount === null || amount < 1 || tender === null;
        applied += amount ?? 0;
        received += tender ?? 0;
        missing += Math.max(0, (amount ?? 0) - (tender ?? 0));
        change += Math.max(0, (tender ?? 0) - (amount ?? 0));
    }
    const sufficient = cash.length > 0 && !invalid && missing === 0;
    return {
        hasCash: cash.length > 0,
        sufficient,
        invalid,
        appliedLabel: `${formatCents(applied)} €`,
        receivedLabel: `${formatCents(received)} €`,
        balanceLabel: invalid
            ? '—'
            : `${formatCents(sufficient ? change : missing)} €`,
    };
}

function formatCents(cents) {
    return `${Math.trunc(cents / 100)},${String(cents % 100).padStart(2, '0')}`;
}

export function createCashTender(received, dueCents) {
    return {
        received,
        dueCents,
        history: [],
        get receivedCents() {
            return parseCashCents(this.received);
        },
        get sufficient() {
            return cashTenderSufficient(this.received, this.dueCents);
        },
        get changeCents() {
            return this.sufficient ? this.receivedCents - this.dueCents : 0;
        },
        get receivedLabel() {
            return this.receivedCents === null
                ? '—'
                : `${formatCents(this.receivedCents)} €`;
        },
        get balanceLabel() {
            return `${formatCents(Math.abs((this.receivedCents ?? 0) - this.dueCents))} €`;
        },
        add(cents) {
            const current = this.received === '' ? 0 : this.receivedCents;
            if (
                current === null ||
                !Number.isInteger(cents) ||
                cents < 1 ||
                current + cents > MAX_CENTS
            )
                return;
            this.history.push(this.received);
            this.received = formatCents(current + cents);
        },
        undo() {
            if (this.history.length > 0) this.received = this.history.pop();
        },
        clear() {
            this.history.push(this.received);
            this.received = '';
        },
        exact() {
            if (
                !Number.isInteger(this.dueCents) ||
                this.dueCents < 1 ||
                this.dueCents > MAX_CENTS
            )
                return;
            this.history.push(this.received);
            this.received = formatCents(this.dueCents);
        },
    };
}
